import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Bed, Cake, Clock, Eye, LogIn, MoonStar, NotebookPen, ShieldAlert, Sparkles, Users, Zap } from "lucide-react";
import { Button, Card, IconButton, TableAvatar, TablePill, TableStatus, Typography, type TableTone } from "@akuna/ui";
import { colorDotClass, colorDotStyle } from "../accommodations/colors";
import { SIZE_TONE, STATUS_TONE, type PanelPet, type PanelPetCardDetails } from "./panelModel";

const NATURE_TONE: Record<PanelPet["nature"], TableTone> = {
  daycare: "orange",
  hotel: "purple",
  both: "blue",
};

export interface PetCardProps {
  pet: PanelPet;
  details?: PanelPetCardDetails;
  /** `overnight_config.is_enabled` — sem isso a clínica não oferece pernoite. */
  overnightEnabled: boolean;
  onCheckIn: (pet: PanelPet) => void;
  onFollowUp: (pet: PanelPet) => void;
  onOvernight: (pet: PanelPet) => void;
}

export function PetCard({ pet, details, overnightEnabled, onCheckIn, onFollowUp, onOvernight }: PetCardProps) {
  const { t } = useTranslation("daycareHotel");
  const isHotel = pet.nature === "hotel" || pet.nature === "both";
  const isFinished = pet.status === "checked_out";
  const alertCount = pet.alertCount ?? pet.alerts.length;
  // Pernoite é para quem passa o dia e vai dormir; no hotel o pet já dorme lá.
  const canAddOvernight = overnightEnabled && pet.nature === "daycare" && !pet.isOvernight;

  return (
    <Card className={`flex flex-col p-4.5 ${isFinished ? "opacity-65 grayscale" : ""}`}>
      <div className="flex items-center gap-3">
        <TableAvatar className="h-10 w-10" name={pet.petName} />
        <div className="min-w-0 flex-1">
          <Link
            to={`/patients/pets/${pet.petId}`}
            className="block truncate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:underline"
          >
            <Typography variant="h5" className="truncate leading-tight">{pet.petName}</Typography>
          </Link>
          {pet.ownerId ? (
            <Link
              to={`/patients/owners/${pet.ownerId}`}
              className="block truncate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:underline"
            >
              <Typography variant="caption" className="truncate text-muted-foreground">{pet.ownerName}</Typography>
            </Link>
          ) : (
            <Typography variant="caption" className="truncate text-muted-foreground">{pet.ownerName}</Typography>
          )}
        </div>
        <TableStatus tone={STATUS_TONE[pet.status]} label={t(`schedule.status.${pet.status}`)} />
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <TablePill tone={NATURE_TONE[pet.nature]}>{t(`schedule.nature.${pet.nature}`)}</TablePill>
        {pet.size !== "unknown" && (
          <TablePill tone={SIZE_TONE[pet.size]}>{t(`panel.size${pet.size[0].toUpperCase()}${pet.size.slice(1)}`)}</TablePill>
        )}
        {details?.packs.map((pack) => (
          <TablePill key={pack.name} tone="neutral">
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className={`h-2 w-2 rounded-full ${colorDotClass(pack.color)}`} style={colorDotStyle(pack.color)} />
              {pack.name}
            </span>
          </TablePill>
        ))}
        {pet.arrivalShift && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1">
            <Clock aria-hidden="true" size={14} className="text-neutral-400" />
            <Typography variant="caption" className="font-semibold text-neutral-600">
              {t(pet.arrivalShift === "morning" ? "shiftMorning" : "shiftEvening")}
            </Typography>
          </span>
        )}
        {pet.period && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1">
            <Clock aria-hidden="true" size={14} className="text-neutral-400" />
            <Typography variant="caption" className="font-semibold text-neutral-600">
              {t(`period${pet.period[0].toUpperCase()}${pet.period.slice(1)}`)}
            </Typography>
          </span>
        )}
        {pet.inAdaptation && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-akuna-amber/40 bg-amber-50 px-2.5 py-1">
            <Sparkles aria-hidden="true" size={14} className="text-akuna-amber" />
            <Typography variant="caption" className="font-semibold text-amber-800">{t("panel.inAdaptation")}</Typography>
          </span>
        )}
        {pet.isOvernight && pet.nature === "daycare" && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-akuna-purple/35 bg-purple-50 px-2.5 py-1">
            <MoonStar aria-hidden="true" size={14} className="text-akuna-purple" />
            <Typography variant="caption" className="font-semibold text-purple-800">{t("badgeOvernight")}</Typography>
          </span>
        )}
        {pet.isBirthday && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-pink-300 bg-pink-50 px-2.5 py-1">
            <Cake aria-hidden="true" size={14} className="text-pink-600" />
            <Typography variant="caption" className="font-semibold text-pink-800">{t("badgeBirthday")}</Typography>
          </span>
        )}
      </div>

      {isHotel && pet.bedLabel && (
        <div className="mt-2.5 flex items-center gap-2 rounded-xl border border-akuna-purple/25 bg-purple-50 px-3 py-2.5">
          <Bed aria-hidden="true" size={16} className="shrink-0 text-akuna-purple" />
          <Typography variant="body-sm" className="truncate font-semibold text-purple-800">{pet.bedLabel}</Typography>
          {pet.stayRange && (
            <Typography variant="caption" className="ml-auto shrink-0 text-purple-700">{pet.stayRange}</Typography>
          )}
        </div>
      )}

      {details?.nextActivity && (
        <div className="none mt-2.5 flex items-center gap-2 rounded-xl bg-muted px-3 py-2.5">
          <Clock aria-hidden="true" size={16} className="shrink-0 text-muted-foreground" />
          <Typography variant="caption" className="shrink-0 font-semibold text-foreground">{details.nextActivity.time}</Typography>
          <Typography variant="caption" className="truncate text-muted-foreground">{details.nextActivity.label}</Typography>
        </div>
      )}

      {alertCount > 0 && (
        <Typography
          className="pl-3 pt-2 flex gap-2 w-full justify-start text-state-danger font-medium"
          variant="body"
        >
          <ShieldAlert aria-hidden="true" size={16} /> {t("panel.petHasAlerts")}
        </Typography>
      )}

      {pet.incompatibleWith.length > 0 && (
        <div className="hidden mt-2 flex items-center gap-2">
          <Zap aria-hidden="true" size={14} className="shrink-0 text-akuna-amber" />
          <Typography variant="caption" className="text-amber-800">
            {t("panel.incompatibleWith", { names: pet.incompatibleWith.join(", ") })}
          </Typography>
        </div>
      )}

      <div className="mt-auto flex items-center gap-2 pt-3 [&>*:first-child]:flex-1">
        {isFinished ? (
          <Button variant="outline" onClick={() => onFollowUp(pet)}>
            <Eye aria-hidden="true" size={16} /> {t("panel.viewDetails")}
          </Button>
        ) : pet.status === "reserved" ? (
          <Button onClick={() => onCheckIn(pet)}>
            <LogIn aria-hidden="true" size={16} /> {t("panel.doCheckIn")}
          </Button>
        ) : (
          <Button variant="outline" onClick={() => onFollowUp(pet)}>
            <NotebookPen aria-hidden="true" size={16} /> {t("panel.follow")}
          </Button>
        )}
        {canAddOvernight && (
          <IconButton
            label={t("panel.addOvernight")}
            variant="outline"
            onClick={() => onOvernight(pet)}
          >
            <MoonStar aria-hidden="true" size={18} className="text-akuna-purple" />
          </IconButton>
        )}
      </div>
    </Card>
  );
}
